import $ from 'jquery';
import {ReactElement} from 'react';
import ReactDOMServer from 'react-dom/server';
import {UAParser} from 'ua-parser-js';
import _ from 'underscore';

import {assert} from 'Asserts';
import throttle from 'util/throttle';
import {BasicEvent, ElementMouseEvent} from 'util/Types';

import {isBrowser, isNodeTesting} from './lib/Browser';
import navigateToURL from './lib/navigateToURL';

const serverBootstrap = {
  origin: '',
};

export function __serverBootstrap(browserContext: object): void {
  Object.assign(serverBootstrap, browserContext);
}

const ASSUME_FAILED_TIMEOUT = 120000;

export const chrome = isBrowser && window.chrome != null;
export const firefox = isBrowser && navigator.userAgent.indexOf('Firefox') !== -1;
export const safari = isBrowser && new UAParser().getBrowser().name === 'Safari';
const hasMimeTypes = isBrowser && navigator.mimeTypes;
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const hasFlash = hasMimeTypes && (navigator.mimeTypes as any)['application/x-shockwave-flash'] != null;
export const svgSupportsForeignObject =
  isBrowser && document.implementation.hasFeature('http://w3.org/TR/SVG11/feature#Extensibility', '1.1');
export const navigatorLanguage = isBrowser && navigator ? navigator.language : null;

export const isMac = (): boolean => isBrowser && navigator.userAgent.indexOf('Mac OS X') > -1;
const IDLE_TIMEOUT_PATH = '/session-expired';

/**
 * Utility for deciding if click should be left up to browser.
 * @return whether event is a simple click, as opposed to a ctrl/cmd/alt/shift/middle/right click.
 */
export const isSimpleClick = (
  e: Pick<ElementMouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>
): boolean => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

export function redirectForDownload(url: string): void {
  setLocationHref(url);
}
// Simple function to navigate but handle ctrl/cmd click, etc.
export function navigate(e: BasicEvent | null, url?: string): void {
  if (e != null && !isSimpleClick(e as ElementMouseEvent)) {
    return;
  }
  if (e) {
    e.preventDefault();
  }
  if (url == null) {
    url = $(e!.currentTarget).attr('href');
  }
  if (url == null) {
    throw new Error('No URL found in navigate call');
  }
  navigateToURL(url);
}

export function reload(query: string = ''): void {
  assert(query.length === 0 || query[0] === '?', 'Invalid URL query');
  setLocationHref(query);
}

export function shouldSuppressNotification(
  res?: {__suppress_notification?: boolean},
  ajaxOptions?: {__suppress_notification?: boolean}
): boolean {
  // Have to check both arguments because __suppress_notification can be set on
  // a Deferred object or an XHR object.
  //
  // - A Deferred object calls error handlers with
  //   (res), i.e., whatever was passed to .reject().
  // - An XHR object calls error handlers with
  //   (xhr, textStatus|ajaxOptions, errorThrown|ajaxOptions).
  return Boolean(
    (res && res.__suppress_notification) || (ajaxOptions && ajaxOptions.__suppress_notification)
  );
}

const activeRequests: {[key: string]: JQueryPromise<unknown>} = {};
const throttleCallbacks: {[key: string]: () => JQueryPromise<unknown>} = {};

// Wraps `request`, a promise, that either resolves/rejects when that promise resolves/rejects or rejects
// when the stale threshold is crossed. This threshold exists because we have found that if a computer is
// slept during an XHR, the XHR is neither resolved nor rejected... and we currently don't have a better
// way of handling it.
// An aggressive timeout means that this case is resolved quicker without many error messages... but at the
// cost of longer entries to start overlapping on saves which can result in lock timeouts.
export function requestFinishedOrTimedOut(request: JQueryPromise<unknown>): JQueryPromise<unknown> {
  const deferred = $.Deferred();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  setTimeout(deferred.reject, ASSUME_FAILED_TIMEOUT);
  // eslint-disable-next-line @typescript-eslint/unbound-method
  request.then(deferred.resolve, deferred.reject);
  return deferred.promise();
}

// Helper method for asyncThrottle. Call only if activeRequests[namespace] is unset.
function _throttleDoCallback(namespace: string): void {
  assert(activeRequests[namespace] == null, 'Invalid request namespace');
  // 1. Pop the callback...
  const callback = throttleCallbacks[namespace];
  if (callback == null) {
    return;
  }
  delete throttleCallbacks[namespace];

  // 2. Call the callback (if it is defined)
  const request = callback();
  if (request.always == null) {
    throw new Error('asyncThrottle was passed a request that does not return an XHR');
  }
  // Note: If callback throws an error or does not return an XHR, activeRequests[namespace] will still be
  // unset, so the next call to asyncThrottle should happen immediately.
  activeRequests[namespace] = request;

  // 3. After the request finishes, remove it as the activeRequest, and call the callback.
  requestFinishedOrTimedOut(request).always(() => {
    delete activeRequests[namespace];
    _throttleDoCallback(namespace);
  });
}

// Does a throttled call to the promise-returning callback, such that when invoked repeatedly for a
// given namespace, will only call the latest callback at most once per every unresolved promise.
// Note: A request might silently fail (e.g. the computer sleeps during an XHR),
//       which would typically prevent any callbacks in the current namespace. If the request does not
//       finish within a threshold, we assume that the request was dropped and fire the callback.
export function asyncThrottle(namespace: string, callback: () => JQueryPromise<unknown>): void {
  // Set(/overwrite) any callback in the given namespace
  throttleCallbacks[namespace] = callback;
  // If there is no active request, call the callback now.
  // Otherwise, the request will call the callback after the request finishes.
  if (activeRequests[namespace] == null) {
    _throttleDoCallback(namespace);
  }
}

export function formatCurrency(cents: number): string {
  if (cents >= 0) {
    return `$${(cents / 100).toFixed(2)}`;
  }
  return `-$${(cents / -100).toFixed(2)}`;
}

const VOWELS = ['a', 'e', 'i', 'o', 'u'];

/*
 * Keep in sync with benchling.lib.helpers.get_plural
 */
export function pluralize(amount: number, name: string, plural?: string): string {
  if (amount === 1) {
    return name;
  }
  if (plural != null) {
    return plural;
  }

  const lastLetter = name.charAt(name.length - 1).toLowerCase();
  const secondLastLetter = name.charAt(name.length - 2).toLowerCase();

  // Replace "is" with "es" (e.g. analysis -> analyses)
  if (secondLastLetter === 'i' && lastLetter === 's') {
    return `${name.slice(0, name.length - 2)}es`;
  }
  // Append "es" when ending in "x", "s", "ch", or "sh"
  // (e.g. box -> boxes, status -> statuses, batch -> batches, crash -> crashes)
  if (['x', 's'].includes(lastLetter) || (lastLetter === 'h' && ['c', 's'].includes(secondLastLetter))) {
    return `${name}es`;
  }
  // Transform "y" => "ies" except when preceded by a vowel (e.g. history -> histories, but not key -> keies)
  if (lastLetter === 'y' && !VOWELS.includes(secondLastLetter)) {
    return `${name.slice(0, -1)}ies`;
  }
  return `${name}s`;
}

/**
 * Returns 'a' or 'an' before the given name, depending on if the name begins with a vowel.
 * @param name
 * @returns {string}
 */
export function prependAOrAn(name: string): string {
  if (name === '') {
    return '';
  }
  const firstLetter = name.toLowerCase().charAt(0);
  if (VOWELS.includes(firstLetter)) {
    return `an ${name}`;
  }
  return `a ${name}`;
}

export function sum<T>(
  obj: ArrayLike<T>,
  iterator?: (context: unknown, value: unknown, index: number, list: Array<unknown>) => number,
  context?: unknown
): number {
  if (!iterator && _.isEmpty(obj)) {
    return 0;
  }
  let result = 0;
  // eslint-disable-next-line no-restricted-syntax
  if (!iterator && _.isArray(obj)) {
    for (let i = obj.length - 1; i >= 0; i--) {
      result += obj[i];
    }
    return result;
  }
  // eslint-disable-next-line no-restricted-syntax
  _.each(obj, (value, index, list) => {
    const computed = iterator ? iterator.call(context, value, index, list) : value;
    result += computed;
  });
  return result;
}

export function repeat<T>(obj: Array<T>, times: number): Array<T> {
  let result = obj.slice(0, 0);
  for (let i = 0; i < times; i++) {
    result = result.concat(obj);
  }
  return result;
}

interface ParsedURI {
  [key: string]: string | {[key: string]: string};
  queryKey: {[key: string]: string};
}
/**
 * Takes in a url string and parses it into an object containing different pieces of the URL
 * Adapted from:
 * parseUri 1.2.2
 * (c) Steven Levithan <stevenlevithan.com>
 * MIT License
 * http://blog.stevenlevithan.com/archives/parseuri
 */
export function parseUri(str: string): ParsedURI {
  const options = {
    strictMode: false,
    key: [
      'source',
      'protocol',
      'authority',
      'userInfo',
      'user',
      'password',
      'host',
      'port',
      'relative',
      'path',
      'directory',
      'file',
      'query',
      'anchor',
    ],
    q: {
      name: 'queryKey',
      parser: /(?:^|&)([^&=]*)=?([^&]*)/g,
    },
    parser: {
      strict:
        /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/, // oxlint-disable-line no-useless-escape
      loose:
        /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/, // oxlint-disable-line no-useless-escape
    },
  };
  const m = options.parser[options.strictMode ? 'strict' : 'loose'].exec(str);
  const uri: Partial<ParsedURI> = {};
  let i = 14;

  while (i--) {
    uri[options.key[i]] = m![i] || '';
  }

  uri[options.q.name] = {};
  // @ts-expect-error
  (uri[options.key[12]] as string).replace(options.q.parser, ($0: string, $1: string, $2: string) => {
    if ($1) {
      (uri[options.q.name] as {[key: string]: string})[$1] = $2;
    }
  });

  return uri as ParsedURI;
}

export function setURLParams(
  url: string,
  values: object,
  defaultsToIgnore: {[key: string]: string} = {}
): string {
  const {path, uriQueryKey} = parseUri(url);
  // URI decode all values first.
  const queryKey: {[key: string]: string} = _.object(
    // eslint-disable-next-line no-restricted-syntax
    _.map(_.pairs(uriQueryKey), ([key, value]) => [key, decodeURIComponent(value)])
  );
  const iterable = _.pairs(values);
  for (let i = 0; i < iterable.length; i++) {
    const [key, value] = iterable[i];
    if (value == null || defaultsToIgnore[key] === value) {
      delete queryKey[key];
    } else {
      queryKey[key] = value;
    }
  }
  let params;
  if (_.isEmpty(queryKey)) {
    params = '';
  } else {
    params = `?${$.param(queryKey)}`;
  }
  return path + params;
}

export const getQueryParam = (key: string): string | null => {
  const parsedUrl = parseUri(window.location.href);
  if (parsedUrl.queryKey[key]) {
    return decodeJQueryParam(parsedUrl.queryKey[key]);
  }
  return null;
};

export const setQueryParam = (key: string, val: string | null): void => {
  const url = setURLParams(window.location.href, {[key]: val});
  window.history.replaceState(null, '', url);
};

// decodeURIComponent is useless, need to replace + ourselves.
export const decodeJQueryParam = (param: string): string => decodeURIComponent(param.replace(/\+/g, ' '));

export const trimLower = (str: string): string => str.trim().toLowerCase();

export const fullPath = (): string =>
  window.location.pathname + window.location.search + window.location.hash;

export function getLocationOrigin(): string {
  return isBrowser || isNodeTesting ? window.location.origin : serverBootstrap.origin;
}

export function pathToURL(path: string): string {
  return `${getLocationOrigin()}${path}`;
}

/**
 * Get the current URL.
 * The purpose of this function is to be stubbed in tests, since `window.location` is nonconfigurable in jsdom
 * >=21.
 */
export function getLocationHref(): string {
  return window.location.href;
}

/**
 * Set the current URL.
 * The purpose of this function is to be stubbed in tests, since `window.location` is nonconfigurable in jsdom
 * >=21.
 */
export function setLocationHref(href: string): void {
  window.location.href = href;
}

/**
 * Get the current pathname.
 * The purpose of this function is to be stubbed in tests, since `window.location` is nonconfigurable in jsdom
 * >=21.
 */
export function getLocationPathname(): string {
  return window.location.pathname;
}

/** Get the location hash without the leading '#' */
export function getLocationHash(): string | undefined {
  return getEntireLocationHash().slice(1);
}

export function getEntireLocationHash(): string {
  return window.location.hash;
}

/** Set the location hash without pushing the change to the user's history. Passing null will fully clear the location hash */
export function setLocationHash(hash: string | null): void {
  window.history.replaceState(
    null,
    '',
    window.location.pathname + window.location.search + (hash == null ? '' : `#${hash}`)
  );
}

/** Remove the hash from the url (does not affect history) */
export function clearLocationHash(): void {
  setLocationHash(null);
}

/*
 * Returns relative URL if the url matches the current origin. Otherwise, returns null.
 */
export function getRelativeURL(url: string): string | null {
  const {host, port, relative} = parseUri(url);
  const {host: locationHost, port: locationPort} = parseUri(getLocationOrigin());
  if (host === locationHost && port === locationPort) {
    return relative as string;
  }
  return null;
}

export function setWindowTitle(title?: string): void {
  if (_.isEmpty(title)) {
    window.document.title = 'Benchling';
  } else {
    window.document.title = `${title} \u00b7 Benchling`;
  }
}

export function redirectTo(url: string): void {
  window.location.assign(url);
}

export function redirectToSignin(next: string = fullPath()): void {
  setLocationHref(setURLParams('/signin', {next}));
}

export function redirectToAdminTools(): void {
  setLocationHref(setURLParams('/internal-admin/tools', {}));
}

export function redirectToSignup(): void {
  setLocationHref(setURLParams('/signup', {}));
}

export function redirectToTimeout(): void {
  if (window.location.pathname !== IDLE_TIMEOUT_PATH) {
    $(window).off('beforeunload'); // disable "Changes you made may not be saved" popup
    setLocationHref(IDLE_TIMEOUT_PATH);
  }
}

export function redirectToLinkGoogleAccount(institution: string): void {
  setLocationHref(
    setURLParams('/ext/google-oauth/link', {
      next: fullPath(),
      institution,
    })
  );
}

// Returns an object {k: v}. This was helpful for creating objects with variable keys in coffeescript,
// e.g. when creating reactUpdate objects.
export const kv = (k: string | number, v: unknown): {[key: string | number]: unknown} => _.object([k], [v]);

export const reactElementToTextContent = (reactElement: ReactElement): string =>
  $(ReactDOMServer.renderToStaticMarkup(reactElement)).text();

export function contactBenchlingViaEmail(): void {
  const mailToLink = 'mailto:support@benchling.com';
  window.open(mailToLink);
}

/**
 * Wrap a function to make it usable as a resize handler on window (See: T2160)
 *
 * 1. Throttling reduces the number of calls since it will fire more frequently than the screen refresh
 * 2. _.defer ensures that the resize event is handled in a new stack, to avoid a suspected Firefox bug,
 *    where the resize event is fired in the middle of an existing callstack.
 */
export function resizeThrottler(fn: () => void): () => void {
  return throttle(() => {
    _.defer(fn);
  }, 16);
}

// benchlingUtil.browser is deprecated, use lib/browser instead!
export const browser = isBrowser;

/**
 * Returns an English sentence fragment listing the items in `items`. If there
 * are more than `maxItemsToDisplay + 1`, displays the first `maxItemsToDisplay`
 * followed by "and {n} others." If there are exactly `maxItemsToDisplay + 1`,
 * displays all of them, to avoid saying "and 1 other".
 *
 * The `conjuction` argument exists to support sentences in the negative like
 * "You don't have access to blah, toot or spuz", where "or" is appropriate and
 * "and" would be incorrect.
 */
// Partially cribbed from `getMemberText` in JoinOrganizationListing.tsx
// “T005, V101 and 3 others.”
// “T005, V101 and V102.”
// “T005 and V101.”
// “T005.”
export function abbreviateList(
  items: Array<string>,
  maxItemsToDisplay: number,
  conjunction: string = 'and'
): string {
  assert(maxItemsToDisplay >= 2, 'abbreviateList: maxItemsToDisplay must be at least 2');
  if (items.length === 0) {
    return '';
  } else if (items.length > maxItemsToDisplay + 1) {
    return `${items.slice(0, maxItemsToDisplay).join(', ')} ${conjunction} ${
      items.length - maxItemsToDisplay
    } others`;
  } else if (items.length > 1) {
    return `${items.slice(0, -1).join(', ')} ${conjunction} ${items[items.length - 1]}`;
  } else {
    return `${items[0]}`;
  }
}
